<template>
  <v-container fluid class="bom-records-container">
    <v-row>
      <v-col cols="12">
        <v-expansion-panels v-model="searchPanel">
          <v-expansion-panel>
            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
              Search Form
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-form @submit.prevent="applyFilters">
                <v-row class="pa-2">
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="filters.code" label="BOM Code" variant="outlined"
                      density="compact" bg-color="white" clearable autocomplete="off"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="filters.productId" :items="productOptions"
                      item-title="label" item-value="id" label="Product"
                      variant="outlined" density="compact" bg-color="white" clearable
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-select
                      v-model="filters.status" :items="statusOptions" label="Status"
                      variant="outlined" density="compact" bg-color="white" clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex flex-wrap ga-2 align-start">
                    <v-btn color="primary" variant="flat" type="submit">Search</v-btn>
                    <v-btn class="bg-grey-darken-1" @click="resetFilters">Reset</v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>

      <v-col cols="12">
        <v-expansion-panels v-model="recordsPanel">
          <v-expansion-panel>
            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
              BOM Records
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row class="py-2" align="center">
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="search" append-inner-icon="mdi-magnify" label="Search"
                    single-line hide-details density="compact" variant="outlined"
                    bg-color="white" clearable
                  />
                </v-col>
                <v-col cols="12" md="5" class="d-flex justify-center">
                  <v-btn-group variant="outlined" density="compact" divided>
                    <v-btn prepend-icon="mdi-download" variant="flat" color="success"
                      :disabled="!filteredRecords.length" @click="exportRecords('csv')">CSV</v-btn>
                    <v-btn prepend-icon="mdi-download" variant="flat" color="primary"
                      :disabled="!filteredRecords.length" @click="exportRecords('excel')">EXCEL</v-btn>
                    <v-btn prepend-icon="mdi-download" variant="flat" color="red"
                      :disabled="!filteredRecords.length" @click="exportRecords('pdf')">PDF</v-btn>
                  </v-btn-group>
                </v-col>
                <v-col cols="12" md="3" class="text-md-right">
                  <v-btn
                    v-if="$insertAccess('/production/add-bom')"
                    :to="{ name: 'production-add-bom' }" color="primary"
                    variant="tonal" prepend-icon="mdi-plus" rounded="xl"
                  >Add BOM</v-btn>
                </v-col>
              </v-row>

              <v-data-table
                v-model:page="page" v-model:items-per-page="itemsPerPage"
                :headers="headers" :items="filteredRecords" item-value="id"
                :loading="loading" hover density="compact"
              >
                <template #item.index="{ index }">
                  {{ (itemsPerPage > 0 ? (page - 1) * itemsPerPage : 0) + index + 1 }}
                </template>
                <template #item.statusLabel="{ item }">
                  <v-chip :color="Number(item.status) === 1 ? 'success' : 'red'" size="small">
                    {{ item.statusLabel }}
                  </v-chip>
                </template>
                <template #item.actions="{ item }">
                  <v-btn
                    icon="mdi-file-document-outline" color="primary" variant="text"
                    size="small" :aria-label="'View BOM ' + item.code"
                    title="View BOM Details" @click="selectedRecord = item"
                  />
                </template>
                <template #no-data>
                  <div class="py-10 text-center text-medium-emphasis">
                    <v-icon icon="mdi-file-document-outline" size="32" class="mb-2" />
                    <div class="font-weight-bold mb-1">
                      {{ records.length ? 'No matching BOM records' : 'No BOM records found' }}
                    </div>
                    <div class="text-body-2">
                      {{ records.length ? 'Try changing or resetting your search filters.' : 'BOM records will appear here once available.' }}
                    </div>
                  </div>
                </template>
              </v-data-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <v-dialog :model-value="!!selectedRecord" max-width="900" @update:model-value="selectedRecord = null">
      <v-card v-if="selectedRecord">
        <v-card-title class="panel-header d-flex align-center">
          BOM Details
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" aria-label="Close BOM details" @click="selectedRecord = null" />
        </v-card-title>
        <v-card-text>
          <v-row class="mb-2">
            <v-col cols="12" sm="4"><strong>BOM Code</strong><div>{{ selectedRecord.code || '—' }}</div></v-col>
            <v-col cols="12" sm="5"><strong>Product</strong><div>{{ selectedRecord.productName }}</div></v-col>
            <v-col cols="12" sm="3"><strong>Status</strong><div>{{ selectedRecord.statusLabel }}</div></v-col>
            <v-col cols="12"><strong>Description</strong><div class="description">{{ selectedRecord.description || '—' }}</div></v-col>
          </v-row>
          <v-table density="compact">
            <thead><tr><th>SL</th><th>Product Code</th><th>Raw Material</th><th>Unit</th><th>Quantity</th></tr></thead>
            <tbody>
              <tr v-for="(material, index) in selectedRecord.materials" :key="material.product_id">
                <td>{{ index + 1 }}</td>
                <td>{{ material.product?.code || material.code || '—' }}</td>
                <td>{{ material.product?.name || material.name || '—' }}</td>
                <td>{{ material.product?.unit?.name || material.unit?.name || '—' }}</td>
                <td>{{ material.quantity }}</td>
              </tr>
              <tr v-if="!selectedRecord.materials.length"><td colspan="5" class="text-center">No raw materials found</td></tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn color="primary" @click="selectedRecord = null">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
const emptyFilters = () => ({ code: '', productId: null, status: null });

export default {
  name: 'ManufacturingBomRecords',
  // Production BOM persistence is not available yet. Keep this data source separate
  // from getProductionBomList, which contains Buying House style/fabric BOMs.
  props: {
    records: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      searchPanel: 0,
      recordsPanel: 0,
      search: '',
      filters: emptyFilters(),
      appliedFilters: emptyFilters(),
      page: 1,
      itemsPerPage: 10,
      selectedRecord: null,
      statusOptions: [{ title: 'Active', value: 1 }, { title: 'Inactive', value: 0 }],
      headers: [
        { title: 'SL', key: 'index', sortable: false },
        { title: 'BOM Code', key: 'code' },
        { title: 'Product Code', key: 'productCode' },
        { title: 'Product Name', key: 'productName' },
        { title: 'Description', key: 'description' },
        { title: 'Raw Materials', key: 'materialCount', align: 'center' },
        { title: 'Status', key: 'statusLabel', align: 'center' },
        { title: 'Actions', key: 'actions', sortable: false, align: 'center' },
      ],
    };
  },
  computed: {
    normalizedRecords() {
      return this.records.map(record => ({
        ...record,
        productCode: record.product?.code || '',
        productName: record.product?.name || '',
        materials: record.materials || [],
        materialCount: record.materials?.length || 0,
        statusLabel: Number(record.status) === 1 ? 'Active' : 'Inactive',
      }));
    },
    productOptions() {
      const products = new Map();
      this.records.forEach(record => {
        const id = record.product_id ?? record.product?.id;
        if (id != null) products.set(String(id), {
          id: String(id), label: [record.product?.code, record.product?.name].filter(Boolean).join(' — '),
        });
      });
      return [...products.values()];
    },
    filteredRecords() {
      const { code, productId, status } = this.appliedFilters;
      const query = (this.search || '').trim().toLowerCase();
      return this.normalizedRecords.filter(record => {
        if (code && !String(record.code || '').toLowerCase().includes(code.trim().toLowerCase())) return false;
        if (productId != null && String(record.product_id ?? record.product?.id) !== String(productId)) return false;
        if (status != null && Number(record.status) !== Number(status)) return false;
        return !query || [record.code, record.productCode, record.productName, record.description, record.statusLabel]
          .some(value => String(value || '').toLowerCase().includes(query));
      });
    },
  },
  watch: {
    search() { this.page = 1; },
  },
  methods: {
    applyFilters() {
      this.appliedFilters = { ...this.filters };
      this.page = 1;
    },
    resetFilters() {
      this.filters = emptyFilters();
      this.appliedFilters = emptyFilters();
      this.search = '';
      this.page = 1;
    },
    exportRecords(format) {
      if (!this.filteredRecords.length) return;
      const columns = ['SL', 'BOM Code', 'Product Code', 'Product Name', 'Description', 'Raw Materials', 'Status'];
      const rows = this.filteredRecords.map((record, index) => [
        index + 1, record.code || '', record.productCode, record.productName,
        record.description || '', record.materialCount, record.statusLabel,
      ]);
      const filename = 'bom_records_' + this.$fileDate();
      if (format === 'pdf') this.$exportTableToPDF(columns, rows, 'BOM Records', filename, 'landscape');
      else if (format === 'excel') this.$exportTableToExcel([columns, ...rows], filename);
      else this.$exportTableToCSV([columns, ...rows], filename);
    },
  },
};
</script>

<style scoped>
.bom-records-container { padding: 24px; }
.description { white-space: pre-wrap; overflow-wrap: anywhere; }
@media (max-width: 960px) {
  .bom-records-container { padding: 12px; }
}
</style>
